import styled from "@emotion/styled";
import { YAxisScalingOption, yAxisScalingLabels } from "../constants";
import React, { memo, useCallback, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { dataVizLayout } from "@atlas-int/tokens";
const chartMarginBase = dataVizLayout.defaultChartMargin;
import { TradeFlow } from "@atlas-int/data/types";
import { UIView } from "@atlas-int/data";
import { useClickOutside, ChevronUp, ChevronDown, Button, MenuOption } from "@atlas-int/ui";
import { surface, interactive, spacing, borderRadius, border, controlFontSize, fontSize, dataViz, zIndex, shadow } from "@atlas-int/tokens";

interface YAxisScalingOptionsSelectorProperties {
  view: UIView;
  yAxisScalingOptionsSelectorMargin: number;
  tradeFlow: TradeFlow;
  yAxisScalingOption: string | undefined;
  tradeDirection: string | undefined;
  onYAxisChange: (yAxis: YAxisScalingOption) => void;
}

const YAxisScalingOptionSelectorContainer = styled.div<{
  $containerWidth: number;
}>`
  width: ${({ $containerWidth }) => $containerWidth}px;
  height: 100%;
  position: absolute;
  top: 0px;
  left: 0px;
  padding-bottom: ${chartMarginBase.bottom}px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: ${dataViz.axisText};
  box-sizing: border-box;
  pointer-events: none;
`;

const YAxisScalingOptionButton = styled(Button)`
  position: relative;
  pointer-events: all;
`;

/** Chevron toggle area — matches the 28×28 circular DropdownToggleButton spec,
 *  rotated 90° so chevrons become left/right in the button's vertical writing-mode. */
const ChevronSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  flex-shrink: 0;
  transform: rotate(90deg);

  &:hover {
    background-color: ${surface.hover};
  }
`;

const YAxisOptionsFlyout = styled.div`
  position: fixed;
  display: flex;
  width: max-content;
  flex-direction: column;
  writing-mode: horizontal-tb;
  list-style-type: none;
  margin: 0;
  padding: 0;
  background-color: ${surface.raised};
  border: 1px solid ${border.control};
  border-radius: ${borderRadius.base};
  box-shadow: ${shadow.sm};
  box-sizing: border-box;
  z-index: ${zIndex.settingsDropdown};
`;

const YAxisOptionsFlyoutOption = styled(MenuOption)`
  text-transform: capitalize;
  min-width: 150px;
  font-size: ${fontSize.base};
  padding: ${spacing[4]} ${spacing[8]};
`;

const YAxisScalingOptionsSelector = ({
  view,
  tradeFlow,
  yAxisScalingOptionsSelectorMargin,
  yAxisScalingOption,
  tradeDirection,
  onYAxisChange,
}: YAxisScalingOptionsSelectorProperties) => {
  const resolvedTradeDirection = tradeDirection;
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const flyoutRef = useClickOutside<HTMLDivElement>(() => setOpen(false), open);
  const [flyoutPos, setFlyoutPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const updateFlyoutPosition = useCallback(() => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    // Position at top-right of the button (matching original MUI Popover anchorOrigin)
    setFlyoutPos({
      top: rect.top,
      left: rect.right,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updateFlyoutPosition();
  }, [open, updateFlyoutPosition]);

  const handleClick = () => {
    setOpen((prev) => !prev);
  };

  const handleClose = () => {
    setOpen(false);
  };

  let flyoutMenuOptions: YAxisScalingOption[] | undefined;
  if (view === UIView.Markets) {
    flyoutMenuOptions = [
      YAxisScalingOption.Current,
      YAxisScalingOption.Constant,
    ];
  } else if (view === UIView.Products) {
    flyoutMenuOptions = [
      YAxisScalingOption.Current,
      YAxisScalingOption.Constant,
      YAxisScalingOption.PerCapita,
      YAxisScalingOption.PerCapitaConstant,
    ];
  }

  let yAxisScalingOptionLabel: string;
  if (yAxisScalingOption === YAxisScalingOption.Current) {
    yAxisScalingOptionLabel = `Current ${tradeFlow} ${resolvedTradeDirection}`;
  } else {
    yAxisScalingOptionLabel = yAxisScalingLabels[yAxisScalingOption as YAxisScalingOption] ?? yAxisScalingOption;
  }

  // In vertical writing-mode (rotated 180°), ChevronUp visually points "open" (rightward)
  // and ChevronDown visually points "closed" (leftward) — matching the horizontal convention
  const ChevronIcon = open ? ChevronUp : ChevronDown;

  return (
    <YAxisScalingOptionSelectorContainer
      $containerWidth={yAxisScalingOptionsSelectorMargin}
      data-download-exclude
    >
      <YAxisScalingOptionButton variant="yAxis" size="md" ref={buttonRef} onClick={handleClick}>
        {yAxisScalingOptionLabel}
        <ChevronSlot>
          <ChevronIcon />
        </ChevronSlot>
      </YAxisScalingOptionButton>
      {open &&
        createPortal(
          <div ref={flyoutRef}>
            <YAxisOptionsFlyout
              style={{ top: flyoutPos.top, left: flyoutPos.left }}
            >
              {flyoutMenuOptions?.map((scalingOption: YAxisScalingOption) => {
                let scalingOptionLabel: string;
                if (scalingOption === YAxisScalingOption.Current) {
                  scalingOptionLabel = `Current ${tradeFlow} ${resolvedTradeDirection}`;
                } else {
                  scalingOptionLabel = yAxisScalingLabels[scalingOption];
                }
                const isSelected =
                  yAxisScalingOption !== undefined &&
                  yAxisScalingOption === scalingOption;

                const handleScalingOptionClick = () => {
                  onYAxisChange(scalingOption);
                  handleClose();
                };
                return (
                  <YAxisOptionsFlyoutOption
                    key={scalingOption}
                    $selected={isSelected}
                    onClick={handleScalingOptionClick}
                  >
                    {scalingOptionLabel}
                  </YAxisOptionsFlyoutOption>
                );
              })}
            </YAxisOptionsFlyout>
          </div>,
          document.body,
        )}
    </YAxisScalingOptionSelectorContainer>
  );
};

export default memo(YAxisScalingOptionsSelector);
